import { faCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import type { useProductFilters } from '@/hooks/business/catalog'
import type { IProductFilters, IProductSortOrder } from '@/schemas/catalog/productFilters'
import ColorCheckbox from '../ColorCheckbox'
import FilterCheckbox from '../FilterCheckbox'
import OrderButtons from '../OrderButtons'
import PriceRangeOptions from '../PriceRangeOptions'
import SizeCheckbox from '../SizeCheckbox'

type IFilterListKey = 'feature' | 'size' | 'color'

export interface IFilterOptionsProps {
    /** Prefixo dos ids: o filtro aparece duas vezes (desktop e mobile) e os rótulos precisam apontar para o próprio campo. */
    idPrefix: string
    filters: IProductFilters
    options: ReturnType<typeof useProductFilters>['options']
    isSelected: (key: IFilterListKey, value: string) => boolean
    onToggle: (key: IFilterListKey, value: string) => void
    onOrderChange: (order: IProductSortOrder) => void
    onPriceChange: (from: number | null, to: number | null) => void
    onShortcut: (feature: string) => void
    onReset: () => void
}

const TITLE = 'mt-8 mb-2 text-sm font-extrabold tracking-wider uppercase text-secondary font-sora'

/** Conteúdo do filtro da listagem (material, característica, tamanho, ordem, preço, cor e atalhos). */
const FilterOptions: React.FC<IFilterOptionsProps> = ({
    idPrefix,
    filters,
    options,
    isSelected,
    onToggle,
    onOrderChange,
    onPriceChange,
    onShortcut,
    onReset,
}) => (
    <div>
        <h3 className={TITLE}>Material</h3>
        <div className="flex flex-col flex-wrap justify-start gap-2 max-w-[300px] max-h-[150px] ">
            {options.materials.map((option) => (
                <div key={option.value}>
                    <FilterCheckbox
                        id={`${idPrefix}-material-${option.value}`}
                        label={option.name}
                        checked={isSelected('feature', option.value)}
                        onChange={() => onToggle('feature', option.value)}
                    />
                </div>
            ))}
        </div>
        <h3 className={TITLE}>Característica</h3>
        <div className="flex flex-col flex-wrap justify-start gap-2 max-w-[300px] ">
            {options.characteristics.map((option) => (
                <div key={option.value}>
                    <FilterCheckbox
                        id={`${idPrefix}-feature-${option.value}`}
                        label={option.name}
                        checked={isSelected('feature', option.value)}
                        onChange={() => onToggle('feature', option.value)}
                    />
                </div>
            ))}
        </div>
        <h3 className={TITLE}>Tamanho</h3>
        <div className="flex flex-row flex-wrap justify-start gap-2 max-w-[300px] max-h-[150px]">
            {options.sizes.map((option) => (
                <div key={option.value}>
                    <SizeCheckbox
                        id={`${idPrefix}-size-${option.value}`}
                        label={option.name}
                        checked={isSelected('size', option.value)}
                        onChange={() => onToggle('size', option.value)}
                    />
                </div>
            ))}
        </div>
        <h3 className="mt-8 mb-4 text-sm font-extrabold tracking-wider text-secondary font-sora">ORDENAR</h3>
        <OrderButtons order={filters.order} onChange={onOrderChange} />

        <h3 className="mt-8 text-sm font-extrabold tracking-wider text-secondary font-sora">FAIXA DE PREÇO (R$)</h3>
        <PriceRangeOptions
            idPrefix={idPrefix}
            ranges={options.priceRanges}
            priceFrom={filters.priceFrom}
            priceTo={filters.priceTo}
            onChange={onPriceChange}
        />
        <h3 className="mt-8 mb-4 text-sm font-extrabold tracking-wider text-secondary font-sora">COR</h3>
        <div>
            <div className="flex flex-row flex-wrap justify-start gap-2 max-w-[300px] max-h-[150px]">
                {options.colors.map((color) => (
                    <div key={color.value}>
                        <ColorCheckbox
                            id={`${idPrefix}-color-${color.value}`}
                            value={color.value}
                            bgColor={color.bgColor}
                            checked={isSelected('color', color.value)}
                            onChange={() => onToggle('color', color.value)}
                        />
                    </div>
                ))}
            </div>
            <div className="flex justify-center mt-8">
                <button
                    type="button"
                    className="px-5 py-2 text-white rounded-full font-md bg-secondary hover:bg-secondary-alpha"
                    onClick={onReset}
                >
                    Limpar filtros
                </button>
            </div>
            <hr className="mt-8 border-cta-border-yellow" />
            <div className="flex flex-col mb-2">
                <h3 className="mt-6 mb-2 text-sm font-extrabold tracking-wider uppercase text-secondary font-sora">
                    Veja também:
                </h3>
                <div className="flex flex-col gap-1 ml-4">
                    {options.features.map((feature) => (
                        // biome-ignore lint/a11y/noStaticElementInteractions: atalho de texto da loja; o visual é o de sempre
                        // biome-ignore lint/a11y/useKeyWithClickEvents: idem
                        <div key={feature.value} onClick={() => onShortcut(feature.value)} className="cursor-pointer">
                            <FontAwesomeIcon
                                icon={faCircle}
                                className="text-[9px] w-4 opacity-60 border-orange text-orange rounded-full bg-orange mr-2"
                            />
                            <span className="text-sm font-bold text-orange hover:text-primary">{feature.name}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
)

export default FilterOptions
